/**
 * useProjectVisibility Hook
 * Extracted from MultiProjectContext.tsx
 * Handles project visibility controls (show, hide, toggle)
 */

import { useCallback } from 'react';
import { log } from '@/shared/utils/logger';
import type { ProjectDataWithMeta } from '../types/multiProject';

interface UseProjectVisibilityParams {
  projectsData: Map<string, ProjectDataWithMeta>;
  setVisibleProjectIds: (updater: (prev: Set<string>) => Set<string>) => void;
  setActiveProjectId: (updater: (prev: string | null) => string | null) => void;
  selectProjects: (projectIds: string[], mode?: 'toggle' | 'set' | 'add') => Promise<void>;
  loadProjectData: (projectId: string, options?: { forceReload?: boolean }) => Promise<ProjectDataWithMeta>;
}

export function useProjectVisibility({
  setVisibleProjectIds,
  selectProjects
}: UseProjectVisibilityParams) {

  const toggleProjectVisibility = useCallback(async (projectId: string) => {
    log.debug('MultiProject', `👁️ Toggling visibility for project ${projectId}`);
    await selectProjects([projectId], 'toggle');
  }, [selectProjects]);

  const showProject = useCallback(async (projectId: string) => {
    log.debug('MultiProject', `👁️ Showing project ${projectId}`);

    // Selection owns loading, active context, and stale-request protection.
    await selectProjects([projectId], 'add');
  }, [selectProjects]);

  const hideProject = useCallback((projectId: string) => {
    log.debug('MultiProject', `👁️ Hiding project ${projectId} (keeping in cache)`);
    setVisibleProjectIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(projectId);
      return newSet;
    });
    // If the hidden project is the active pinned one, keep it pinned (views decide rendering)
  }, [setVisibleProjectIds]);

  return {
    toggleProjectVisibility,
    showProject,
    hideProject
  };
}
